import { Company } from '../../company/company.model';
import { Customer } from '../../customer/customer.model';
import { CrmClient } from '../../crm/crm.model';

export type PartyMode = 'SELF' | 'CRM';

export interface ContractPartyOption {
  key: string;
  label: string;
  crmClientId?: number;
  companyId: number;
  companyLabel: string;
  linkedCustomerId?: number | null;
  defaultContactId?: number | null;
  roles: string[];
  company: Company;
  legacy?: boolean;
}

export interface ResolvedPartySelection {
  mode: PartyMode;
  option: ContractPartyOption | null;
}

export function hasSupplierRole(roles?: string[] | null): boolean {
  return (roles || []).some(role => `${role || ''}`.toUpperCase().includes('SUPPLIER'));
}

export function buildContractPartyOptions(clients: CrmClient[], companies: Company[]) {
  const companyById = new Map<number, Company>();
  for (const company of companies || []) {
    if (company?.id) {
      companyById.set(company.id, company);
    }
  }

  const options = (clients || [])
    .filter(client => !!client?.id && !!client.linkedCompanyId && companyById.has(client.linkedCompanyId))
    .map(client => {
      const company = companyById.get(client.linkedCompanyId!)!;
      return {
        key: `crm:${client.id}`,
        label: client.companyName || company.name || `CRM Client #${client.id}`,
        crmClientId: client.id,
        companyId: company.id,
        companyLabel: company.name || client.companyName || `Company #${company.id}`,
        linkedCustomerId: client.linkedCustomerId ?? null,
        defaultContactId: company.defaultContactId ?? null,
        roles: [...(client.roles || [])],
        company
      } as ContractPartyOption;
    })
    .sort((left, right) => left.label.localeCompare(right.label));

  return {
    sellerOptions: options.filter(option => hasSupplierRole(option.roles)),
    buyerOptions: options.filter(option => !hasSupplierRole(option.roles))
  };
}

export function createLegacyPartyOption(company?: Company | null): ContractPartyOption | null {
  if (!company?.id) {
    return null;
  }
  return {
    key: `legacy:${company.id}`,
    label: `${company.name || `Company #${company.id}`} (Legacy)`,
    companyId: company.id,
    companyLabel: company.name || `Company #${company.id}`,
    linkedCustomerId: null,
    defaultContactId: company.defaultContactId ?? null,
    roles: [],
    company,
    legacy: true
  };
}

export function resolvePreferredRepresentative(customers: Customer[], option?: ContractPartyOption | null): Customer | null {
  if (!customers?.length) {
    return null;
  }

  const preferredIds = [option?.linkedCustomerId, option?.defaultContactId]
    .filter((value): value is number => Number.isFinite(value) && Number(value) > 0);

  for (const preferredId of preferredIds) {
    const match = customers.find(customer => customer.id === preferredId);
    if (match) {
      return match;
    }
  }

  return null;
}

export function resolvePartySelection(
  company: Company | undefined | null,
  sessionCompany: Company | undefined | null,
  options: ContractPartyOption[],
  crmClientId?: number | null
): ResolvedPartySelection | null {
  if (!company?.id) {
    return null;
  }

  if (sessionCompany?.id && company.id === sessionCompany.id) {
    return { mode: 'SELF', option: null };
  }

  if (crmClientId) {
    const optionByClient = options.find(option => option.crmClientId === crmClientId);
    if (optionByClient) {
      return { mode: 'CRM', option: optionByClient };
    }
  }

  const optionByCompany = options.find(option => option.companyId === company.id);
  if (optionByCompany) {
    return { mode: 'CRM', option: optionByCompany };
  }

  return { mode: 'CRM', option: createLegacyPartyOption(company) };
}
